import axios, { type AxiosError } from 'axios';

import { ApiResponseError, VersionMismatchError } from './apiError';

function exponentialBackoff(attempt: number, ms: number): number {
  return ms * (2 ** attempt - 1);
}

function linear(attempt: number, ms: number): number {
  return ms * attempt;
}

function geometric(attempt: number, ms: number): number {
  return ms * (2 * (attempt - 1) + 1);
}

function fixed(attempt: number, ms: number): number {
  return ms;
}

export type RetryStrategy =
  | 'exponentialBackoff'
  | 'geometric'
  | 'linear'
  | 'fixed';

function applyDelayStrategy(
  strategy: RetryStrategy,
  attemptNumber: number,
  ms: number,
): number {
  switch (strategy) {
    case 'exponentialBackoff':
      return exponentialBackoff(attemptNumber, ms);
    case 'geometric':
      return geometric(attemptNumber, ms);
    case 'linear':
      return linear(attemptNumber, ms);
    default:
      return fixed(attemptNumber, ms);
  }
}

export const delay = (ms: number): Promise<unknown> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

interface RetrierOptions {
  maxAttempts?: number;
  strategy?: RetryStrategy;
  delayMs?: () => number;
  delayCallback?: () => void;
}

type ResponsePromise<T> = () => Promise<T>;

export const DefaultDelay: () => number = () => 1000;

function isAxiosError(error: any): error is AxiosError {
  return error && (error as AxiosError).isAxiosError !== undefined;
}

export function retrier<T>(
  promise: ResponsePromise<T>,
  {
    maxAttempts = 3,
    strategy = 'fixed',
    delayMs = DefaultDelay,
    delayCallback,
  }: RetrierOptions,
): Promise<T> {
  let attemptNumber = 1;

  async function tryPromise(): Promise<T> {
    try {
      const response = await promise();
      if (typeof response === 'undefined') {
        throw new Error('API response undefined');
      }
      return response;
    } catch (err) {
      if (
        axios.isCancel(err) ||
        (isAxiosError(err) && err.response && err.response.status < 500) ||
        err instanceof VersionMismatchError ||
        (err instanceof ApiResponseError && err.status < 500) ||
        attemptNumber >= maxAttempts
      ) {
        throw err;
      }

      if (delayCallback) {
        delayCallback();
      }

      const ms = applyDelayStrategy(strategy, attemptNumber, delayMs());
      await delay(ms);

      attemptNumber += 1;
      return tryPromise();
    }
  }

  return tryPromise();
}
